import { getDistance } from "geolib";
import type { IsoDate } from "../dates";
import type { PayeeId } from "../ids";
import type { GeoLocation, Payee, Transaction } from "../schemas";

/** The payee with the most transactions within this radius is pre-filled. */
export const guessRadiusMeters = 150;
/** Every payee with a transaction within this radius is listed. */
export const nearbyRadiusMeters = 1000;
export const defaultRecentLimit = 10;

/** Four decimal places is about 11m of latitude: enough to find the shop, not the person. */
export function roundLocation(loc: GeoLocation): GeoLocation {
  return { lat: round4(loc.lat), lng: round4(loc.lng) };
}

function round4(x: number): number {
  // `+ 0` turns −0 into 0 so rounded locations compare and serialise cleanly.
  return Math.round(x * 1e4) / 1e4 + 0;
}

export interface NearbyPayee {
  payeeId: PayeeId;
  /** Distance to the payee's nearest transaction, in metres. */
  distanceMeters: number;
  /** Transactions within the nearby radius. */
  count: number;
}

export interface PayeeSuggestions {
  guess: PayeeId | null;
  nearby: NearbyPayee[];
  recent: PayeeId[];
}

export interface SuggestPayeesInput {
  here: GeoLocation;
  transactions: Transaction[];
  payees: Payee[];
  recentLimit?: number;
}

interface PayeeStats {
  payeeId: PayeeId;
  name: string;
  lastDate: IsoDate;
  nearest: number;
  nearbyCount: number;
  guessCount: number;
  guessNearest: number;
}

function countsForSuggestions(t: Transaction): t is Transaction & { payeeId: PayeeId } {
  return !t.deleted && t.payeeId !== undefined && t.transfer === undefined;
}

export function suggestPayees(input: SuggestPayeesInput): PayeeSuggestions {
  const { here, transactions, payees, recentLimit = defaultRecentLimit } = input;
  const names = new Map<PayeeId, string>();
  for (const p of payees) {
    if (!p.deleted) {
      names.set(p.id, p.name);
    }
  }

  // Locations are rounded, so many transactions share a point; measure each point once.
  const distanceCache = new Map<string, number>();
  const distanceTo = (loc: GeoLocation): number => {
    const key = `${loc.lat},${loc.lng}`;
    let d = distanceCache.get(key);
    if (d === undefined) {
      d = getDistance(here, loc);
      distanceCache.set(key, d);
    }
    return d;
  };

  const stats = new Map<PayeeId, PayeeStats>();
  for (const t of transactions) {
    if (!countsForSuggestions(t)) {
      continue;
    }
    const name = names.get(t.payeeId);
    if (name === undefined) {
      continue;
    }
    let s = stats.get(t.payeeId);
    if (!s) {
      s = {
        payeeId: t.payeeId,
        name,
        lastDate: t.date,
        nearest: Infinity,
        nearbyCount: 0,
        guessCount: 0,
        guessNearest: Infinity,
      };
      stats.set(t.payeeId, s);
    } else if (t.date > s.lastDate) {
      s.lastDate = t.date;
    }
    if (!t.location) {
      continue;
    }
    const d = distanceTo(t.location);
    if (d > nearbyRadiusMeters) {
      continue;
    }
    s.nearbyCount++;
    s.nearest = Math.min(s.nearest, d);
    if (d <= guessRadiusMeters) {
      s.guessCount++;
      s.guessNearest = Math.min(s.guessNearest, d);
    }
  }

  const all = [...stats.values()];
  const byName = (a: PayeeStats, b: PayeeStats) => a.name.localeCompare(b.name);

  const nearbyStats = all
    .filter((s) => s.nearbyCount > 0)
    .sort((a, b) => a.nearest - b.nearest || b.nearbyCount - a.nearbyCount || byName(a, b));

  let guess: PayeeStats | null = null;
  for (const s of nearbyStats) {
    if (s.guessCount === 0) {
      continue;
    }
    if (
      !guess ||
      s.guessCount > guess.guessCount ||
      (s.guessCount === guess.guessCount && s.guessNearest < guess.guessNearest)
    ) {
      guess = s;
    }
  }

  const recent = all
    .filter((s) => s.nearbyCount === 0)
    .sort((a, b) => (a.lastDate < b.lastDate ? 1 : a.lastDate > b.lastDate ? -1 : byName(a, b)))
    .slice(0, Math.max(0, recentLimit))
    .map((s) => s.payeeId);

  return {
    guess: guess?.payeeId ?? null,
    nearby: nearbyStats.map((s) => ({
      payeeId: s.payeeId,
      distanceMeters: s.nearest,
      count: s.nearbyCount,
    })),
    recent,
  };
}

/** The distinct places a payee's transactions were entered, derived rather than stored. */
export function payeeLocations(payeeId: PayeeId, transactions: Transaction[]): GeoLocation[] {
  const seen = new Map<string, GeoLocation>();
  for (const t of transactions) {
    if (t.deleted || t.payeeId !== payeeId || t.transfer !== undefined || !t.location) {
      continue;
    }
    const loc = roundLocation(t.location);
    seen.set(`${loc.lat},${loc.lng}`, loc);
  }
  return [...seen.values()];
}
